import React, { useEffect, useState } from 'react';
import { generalStore } from '../../store';
import { isEmpty } from 'ramda';

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RESPONSE, ROUTES } from '../../js/_constants';
import Helpers, { sanitizeCollectionResponse } from '../../js/helpers';
import Loader from '../../components/loader/Loader';
import GoBackButton from '../../components/goBackButton/GoBackButton';
import Pagination from '../../components/pagination/Pagination';
import { userStore } from '../../store';
import api from '../../api';
import './DevicesView.scss';

type DevicesViewProps = {
};

const DevicesView: React.FC<DevicesViewProps> = () => {
    const { setPageName } = generalStore();
    const { t } = useTranslation();
    const { details } = userStore();
    const controller = new AbortController();
    const signal = controller.signal;
    const [loading, setLoading] = useState(false);
    const [deviceList, setDeviceList] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPages] = useState(1);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');

    const getDevice = async (page, query) => {
        setLoading(true);
        const devicesResponse = await api.devices.getDevices({
            params: {
                brand: details.userGrantedBrands,
                page,
                ...(query && {
                    deviceIdentifier: query,
                    title: query
                }),
            },
            signal
        });
        const sanitizeResponse = sanitizeCollectionResponse(devicesResponse);
        setTotalPages(sanitizeResponse.totalPages);
        setCurrentPages(sanitizeResponse.currentPage);
        setPage(sanitizeResponse.currentPage + 1);
        setDeviceList(devicesResponse[RESPONSE.EXPAND_COLLECTION]);
        setLoading(false);
    };

    useEffect(() => {
        setPageName('page_name_devices');
        return () => {
            setPageName('');
        };
    }, []);

    useEffect(() => {
        const currentSearch = Helpers.getQueryParameter();
        setQuery(currentSearch.query || '');
        getDevice(currentSearch.page, currentSearch.query);
    }, [location.search]);

    const renderDevice = (device, index) => {
        let playlistString = t('device_no_playlist_assigned');

        if (device.playlists.length > 0) {
            playlistString = `${device.playlists
                .sort(Helpers.sortAlphabetically)
                .map(playlist => ` ${playlist.title}`)}.`;
        }

        return (
            <Link
                className="devices-list-item"
                key={device[RESPONSE.ID]}
                to={`${ROUTES.DEALERSHIPS}${ROUTES.DEVICES}/${device.deviceNumber}?brand=${device.brand.split('/').pop()}`}
                data-testid={`device-card-${index + 1}`}
            >
                <div>
                    <p className="device-title">{device.title} {Helpers.isRecentlyAdded(device.createdAt) && <span className='recently-added'>{t('recently_added')}</span>}</p>
                    <p className='device-number'>
                        {t('device_identifiers')}: {device.deviceNumber && <span className="device-id">{device.deviceNumber}</span>}{device.serialNumber && <span className="device-id">{device.serialNumber}</span>}{device.thingName && <span className="device-id">{device.thingName}</span>}
                    </p>
                    <p className="device-playlists-names">
                        {device.playlists.length <= 1
                            ? t('device_playlist')
                            : t('device_playlists')}
                        : {playlistString}
                    </p>
                </div>
                <span className='arrow-back'></span>
            </Link>
        );
    };

    const handlePaginationClick = async (pageNumber: number) => {
        getDevice(pageNumber, query);
    };

    const renderPagination = () => {
        if (totalPages > 1 && !loading) {
            return (
                <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onClick={handlePaginationClick}
                />
            );
        }
    }

    return (
        <div className="b-devices-view">
            <div className='back-button-wrapper'>
                <GoBackButton />
                {query && (
                    <div className="result-message">
                        <p>
                            {t('device_search_results')}{' '}
                            <span>
                                &ldquo; {query} &bdquo;
                            </span>
                        </p>
                    </div>)
                }
            </div>
            <div className="inner-content">
                {loading ? (
                    <Loader loading />
                ) : (
                    isEmpty(deviceList) 
                        ? (<span className="b-devices-view__empty-state-message">
                            {t('devices_view_no_search_results')}
                            </span>)
                        : deviceList.map((device, i) => renderDevice(device, i))
                )}
            </div>
            {renderPagination()}
        </div>
    );
};
export default DevicesView;